import { jsxs, jsx } from "react/jsx-runtime";
import { token } from "@/styled-system/tokens";
import { useCarousel, Carousel } from "@zillow/constellation-10";
import { event } from "@zillow/universal-analytics";
import { usePageProps, getCmsInfoForPage, v8ResponsiveToPanda } from "@zillow/zrs-cms";
import { htmlToDOM } from "html-react-parser";
import { generateBlockList } from "../../components/BlockList/BlockList.esm.js";
const VALID_SLIDE_GAP = ["xxs", "xs", "sm", "md", "lg", "xl"];
const VALID_SCROLL_OFFSET = ["none", "xs", "sm", "md", "lg", "xl"];
function isV8ResponsiveObj(value) {
  return value !== null && typeof value === "object" && ("default" in value || "lg_lte" in value || "sm_lte" in value);
}
function mapSlideGap(str) {
  const s = String(str ?? "sm");
  if (VALID_SLIDE_GAP.includes(s)) return s;
  if (s === "xxl" || s === "xxxl") return "xl";
  return "sm";
}
function mapScrollOffset(scalar) {
  if (scalar === 0 || scalar === "0" || !scalar) return "none";
  const s = String(scalar);
  if (VALID_SCROLL_OFFSET.includes(s)) return s;
  if (s === "xxl" || s === "xxxl") return "xl";
  return "none";
}
function resolveSlideGap(value) {
  if (isV8ResponsiveObj(value)) return v8ResponsiveToPanda(value, mapSlideGap);
  return mapSlideGap(value);
}
function resolveScrollOffset(value) {
  if (isV8ResponsiveObj(value)) return v8ResponsiveToPanda(value, mapScrollOffset);
  return mapScrollOffset(value);
}
function resolveFlexBasis(slideWidth) {
  if (slideWidth == null) return void 0;
  const scalar = isV8ResponsiveObj(slideWidth) ? slideWidth.default : slideWidth;
  if (scalar == null) return void 0;
  if (typeof scalar === "number") return `${scalar}px`;
  const num = Number(scalar);
  return Number.isNaN(num) ? scalar : `${num}px`;
}
function collectText(nodes) {
  return nodes.map((node) => {
    if (node.type === "text") return node.data;
    if (node.type === "tag" && node.name === "br") return " ";
    if ("children" in node) return collectText(node.children);
    return "";
  }).join("");
}
function toPlainText(html) {
  if (typeof html !== "string" || !html) return "";
  return collectText(htmlToDOM(html)).replace(/\s+/g, " ").trim();
}
function getCarouselLabel(headingBlocks) {
  var _a;
  const heading = headingBlocks == null ? void 0 : headingBlocks.find((block) => block.blockName === "constellation/heading");
  const headingText = toPlainText((_a = heading == null ? void 0 : heading.attrs) == null ? void 0 : _a.content);
  return headingText ? `${headingText} carousel` : "Carousel";
}
const SCROLL_OFFSET_TOKEN = {
  xs: token("spacing.xs"),
  // var(--spacing-xs)  → 0.5rem
  sm: token("spacing.sm"),
  // var(--spacing-sm)  → 1rem
  md: token("spacing.md"),
  // var(--spacing-md)  → 1.5rem
  lg: token("spacing.lg"),
  // var(--spacing-lg)  → 2rem
  xl: token("spacing.xl")
  // var(--spacing-xl)  → 3rem
};
const BlockCarousel = ({
  attrs,
  index,
  innerBlocks
}) => {
  const pageProps = usePageProps();
  const {
    slideWidth = 340,
    slideGap = "sm",
    slideAlign = "start",
    scrollOffset = 0,
    includeSkipLink = true,
    enableSnap = false,
    scrollOffsetMargin = false,
    ...restAttrs
  } = attrs;
  const handleNavigationEvent = (navigation) => {
    event({
      // https://dataportal.zillowgroup.net/event-registry/2048
      envelope: {
        event_type_id: "2048",
        event_type_version_id: "3",
        event_template_id: "126",
        event_template_version_id: "1"
        // Master ID: 2284.2.126.1
      },
      clickstream_trigger: {
        trigger_type_nm: "interaction",
        trigger_location_nm: "cms",
        trigger_source_nm: "cms_carousel",
        trigger_object_nm: "no_trigger_object"
      },
      semantic: {
        semantic_event_nm: "cms_click"
      },
      cms_info: {
        block_id: (attrs == null ? void 0 : attrs.id) || "",
        block_type_cd: "carousel",
        block_displayed_txt: navigation,
        ...getCmsInfoForPage(pageProps)
      }
    });
  };
  const {
    previousEnabled,
    nextEnabled,
    nextPage,
    previousPage,
    firstSlide,
    scrollContainer,
    sectionStart,
    sectionEnd,
    handleOnScroll,
    scrollToFocusedEl,
    handleSkipToStart,
    handleSkipToEnd
  } = useCarousel({
    onNextClick: () => handleNavigationEvent("next"),
    onPreviousClick: () => handleNavigationEvent("previous")
  });
  const headingInnerBlocks = innerBlocks.find((block) => block.blockName === "constellation/carousel-v2-heading");
  const slidesInnerBlocks = innerBlocks.find((block) => block.blockName === "constellation/carousel-v2-slides");
  const resolvedSlideGap = resolveSlideGap(slideGap);
  const resolvedScrollOffset = resolveScrollOffset(scrollOffset);
  const flexBasis = resolveFlexBasis(slideWidth);
  const slides = (slidesInnerBlocks == null ? void 0 : slidesInnerBlocks.innerBlocks) ?? [];
  const effectiveOffset = typeof resolvedScrollOffset === "string" ? resolvedScrollOffset : resolvedScrollOffset.xl ?? resolvedScrollOffset.base ?? "none";
  const paddingToken = scrollOffsetMargin && effectiveOffset !== "none" ? SCROLL_OFFSET_TOKEN[effectiveOffset] : void 0;
  const offsetMarginValue = paddingToken ? `calc(-1 * ${paddingToken})` : void 0;
  return /* @__PURE__ */ jsxs(Carousel.Root, { "aria-label": getCarouselLabel(headingInnerBlocks == null ? void 0 : headingInnerBlocks.innerBlocks), ...restAttrs, children: [
    /* @__PURE__ */ jsxs(Carousel.Header, { children: [
      /* @__PURE__ */ jsx(Carousel.HeadingArea, { children: generateBlockList({
        blocks: headingInnerBlocks == null ? void 0 : headingInnerBlocks.innerBlocks
      }) }),
      /* @__PURE__ */ jsx(Carousel.NavControls, { css: {
        gap: "{spacing.default}"
      }, leftButtonProps: {
        onClick: previousPage,
        disabled: !previousEnabled,
        size: "sm"
      }, rightButtonProps: {
        onClick: nextPage,
        disabled: !nextEnabled,
        size: "sm"
      } })
    ] }),
    includeSkipLink && /* @__PURE__ */ jsx(Carousel.SkipLink, { onClick: handleSkipToEnd, ref: sectionStart, children: "Skip to the end of the carousel" }),
    /* @__PURE__ */ jsx(Carousel.ScrollContainer, { enableSnap, hasFocusableContent: true, onKeyUp: scrollToFocusedEl, onScroll: (e) => handleOnScroll(e), scrollOffset: resolvedScrollOffset, slideGap: resolvedSlideGap, ref: scrollContainer, style: offsetMarginValue ? {
      marginInline: offsetMarginValue
    } : void 0, children: slides.map((block, slideIndex) => /* @__PURE__ */ jsx(Carousel.Slide, { ref: slideIndex === 0 ? firstSlide : void 0, slideAlign, style: flexBasis !== void 0 ? {
      flexBasis
    } : void 0, children: generateBlockList({
      blocks: [block]
    }) }, slideIndex)) }),
    includeSkipLink && /* @__PURE__ */ jsx(Carousel.SkipLink, { onClick: handleSkipToStart, ref: sectionEnd, children: "Skip to the beginning of the carousel" })
  ] }, index);
};
export {
  BlockCarousel as default
};
